/*
  pf-guard.js - J1 second layer.

  A made-to-order cushion is priced and specified on its own product page: width, length,
  thickness, non-slip base and the two fabric colours all arrive as line-item properties from
  pf-cc-form.js. Every other surface (quick view, quick add, mega menu, recently viewed, cart
  drawer recommendations, the cart editor) can only post the bare base variant, which becomes an
  order nobody can make. The Liquid snippets already refuse to render those controls, but Ella
  re-renders cards and popups client-side from templates/product.ajax_*.liquid, and a cached or
  half-updated fragment can put a button back. This file is the cheap defence against that.

  It never guesses: a cushion is only a product the server marked with data-pf-cushion="1", and
  ids and handles learned that way are remembered for the visit so a later fragment that arrives
  without the marker is still recognised without a network round trip.

  The customer's own product page is deliberately out of scope - the guard only touches controls
  that sit inside a card, popup or drawer, so the real buy box keeps working.

  Where it cannot work out which product page a fragment belongs to, it disables the control where
  it stands instead of offering a "Choose Options" button with nowhere to go. An inert control is
  honest; one that quietly lands the shopper on the homepage is not.

  Kill switch: ?pf_guard_off=1 (same shape as ?pf_cc_off=1 and ?pf_pdp_off=1).
*/
(function () {
  'use strict';

  if (window.__pfGuardLoaded) return;
  window.__pfGuardLoaded = true;
  if (location.search.indexOf('pf_guard_off=1') > -1) return;

  // English-only store; exposed so the owner can override without editing this file.
  var LABEL = window.PF_GUARD_LABEL || 'Choose Options';

  // Surfaces that are never the product page's own buy box.
  var CTX = '[data-pf-cushion],.product-item,.halo-popup,#halo-quick-view-popup,' +
    '#halo-quick-shop-popup,#halo-quickshop-popup,.halo-quick-bulk,[data-template-cart-edit],' +
    '#halo-side-cart,.halo-recently-viewed-popup,before-you-leave-component,.quick-add,' +
    '.quick-add-modal,.mega-menu,.card-action';

  // Popup shells whose contents arrive over AJAX and therefore need re-checking on every mutation.
  var POPUPS = '#halo-quick-view-popup,#halo-quick-shop-popup,#halo-quickshop-popup,' +
    '.halo-quick-bulk,[data-template-cart-edit]';

  var TRIGGERS = '[data-open-quick-view-popup],[data-open-quick-shop-popup],[data-open-quick-shop],' +
    '[data-open-quick-bulk],[data-open-edit-cart],.quickview-icon,.quickview-button,' +
    '.quickshop-icon,.quickshop-button';

  // Controls that can reach the cart or the checkout without the cushion form.
  var BUY = 'button[name="add"],[data-btn-addToCart],[data-update-cart-edit],.product-form__submit';
  var EXPRESS = '.shopify-payment-button,.productView-payment,shopify-accelerated-checkout';

  /* ---------------------------------------------------------------- registry */

  var STORE = 'pfGuardCushions';
  var ids = {};
  var handles = {};
  try {
    var saved = JSON.parse(sessionStorage.getItem(STORE) || '{}');
    ids = saved.i || {};
    handles = saved.h || {};
  } catch (e) { /* private mode - the DOM marker still works, we just relearn each page */ }

  function persist() {
    try { sessionStorage.setItem(STORE, JSON.stringify({ i: ids, h: handles })); } catch (e) {}
  }

  function handleOf(url) {
    var m = /\/products\/([^/?#]+)/.exec(url || '');
    return m ? m[1] : null;
  }

  // product-card-sidebar prefixes its id with "product-"; normalise before comparing.
  function idOf(value) {
    if (!value) return null;
    return String(value).replace(/^product-/, '') || null;
  }

  // An id is remembered with its handle when we have one, so a later fragment that carries only the
  // id can still be turned into a real product URL instead of a dead end. Older entries stored a
  // plain 1; both shapes are truthy, so the registry stays backward compatible within a visit.
  function note(id, handle) {
    var changed = false;
    if (id) {
      if (!ids[id]) { ids[id] = handle || 1; changed = true; }
      else if (handle && ids[id] !== handle) { ids[id] = handle; changed = true; }
    }
    if (handle && !handles[handle]) { handles[handle] = 1; changed = true; }
    if (changed) persist();
  }

  function knows(id, handle) {
    return !!((id && ids[id]) || (handle && handles[handle]));
  }

  // The product page URL for something we identified, or null. Null means null: nothing here
  // invents a destination.
  function urlOf(who) {
    if (!who) return null;
    if (who.url) return who.url;
    if (who.handle) return '/products/' + who.handle;
    if (who.id && typeof ids[who.id] === 'string') return '/products/' + ids[who.id];
    return null;
  }

  // layout/theme.liquid loads the cushion form assets only when the template suffix is
  // custom-cushion, so their presence is a server-side "this product is a cushion" signal. Taking
  // it here means a visitor who lands straight on a cushion page is still recognised later, in the
  // cart drawer's editor or in a recommendation strip, without asking the network anything.
  if (document.querySelector('script[src*="pf-cc-form"],link[href*="pf-cc-form"]')) {
    note(null, handleOf(location.pathname));
  }

  /* ------------------------------------------------------- reading a fragment */

  // Everything a card or popup can tell us about which product it is showing.
  function identify(el) {
    if (!el) return { id: null, handle: null, url: null };
    var id = idOf(el.getAttribute && el.getAttribute('data-product-id'));
    var handle = (el.getAttribute && el.getAttribute('data-product-handle')) || null;
    var url = null;
    var holder = !id && el.querySelector ? el.querySelector('[data-product-id]') : null;
    if (holder) id = idOf(holder.getAttribute('data-product-id'));
    if (el.querySelector) {
      var pid = el.querySelector('input[name="product-id"]');
      if (!id && pid) id = idOf(pid.value);
      var link = el.querySelector('a[href*="/products/"]');
      if (link) { url = link.getAttribute('href'); if (!handle) handle = handleOf(url); }
    }
    if (!url && handle) url = '/products/' + handle;
    return { id: id, handle: handle, url: url };
  }

  function isCushionNode(el) {
    if (!el || !el.closest) return false;
    if (el.closest('[data-pf-cushion]')) return true;
    var shell = el.closest(POPUPS) || el.closest('.product-item') || el.closest('.card-action');
    if (!shell) return false;
    var who = identify(shell);
    return knows(who.id, who.handle);
  }

  /* ------------------------------------------------------------- neutralising */

  // Only ever called with a real product URL - see neutralise().
  function linkFor(node, url) {
    var a = document.createElement('a');
    // Reuse the control's own classes so the replacement is styled, never a half-drawn button.
    var cls = typeof node.className === 'string' ? node.className : '';
    a.className = cls + ' pf-guard-link';
    a.setAttribute('href', url);
    a.setAttribute('data-pf-guard', 'link');
    a.textContent = LABEL;
    return a;
  }

  // The honest fallback when we cannot work out which product page to send the shopper to: stop the
  // control from reaching the cart, in place, and say so. A "Choose Options" button pointing at the
  // homepage would look like it works and quietly take the shopper somewhere they did not ask for.
  function makeInert(node) {
    node.setAttribute('data-pf-guard', 'inert');
    node.setAttribute('aria-disabled', 'true');
    // <button>/<input> can refuse by themselves; anything else (Ella uses <a data-btn-addToCart>)
    // gets the ARIA equivalent plus a dead pointer, and the capture handlers below stop it too.
    if ('disabled' in node) {
      try { node.disabled = true; } catch (e) {}
    } else {
      node.setAttribute('tabindex', '-1');
      if (node.style) node.style.pointerEvents = 'none';
    }
  }

  function neutralise(scope, url) {
    var n, i, list;
    list = scope.querySelectorAll(BUY);
    for (i = 0; i < list.length; i++) {
      n = list[i];
      var mark = n.getAttribute('data-pf-guard');
      if (mark === 'link') continue;
      // A control parked as inert is upgraded to a real route if we have since learnt the URL.
      if (mark === 'inert' && !url) continue;
      if (url && n.parentNode) n.parentNode.replaceChild(linkFor(n, url), n);
      else if (!mark) makeInert(n);
    }
    list = scope.querySelectorAll(EXPRESS);
    for (i = 0; i < list.length; i++) {
      // An express checkout has no honest replacement - the link above it is the route.
      list[i].parentNode && list[i].parentNode.removeChild(list[i]);
    }
  }

  function sweep() {
    var i, j, list, sub, who;

    // 1. Cards the server marked: drop the quick view / quick add controls outright.
    list = document.querySelectorAll('[data-pf-cushion]');
    for (i = 0; i < list.length; i++) {
      who = identify(list[i]);
      note(who.id, who.handle);
      sub = list[i].querySelectorAll(TRIGGERS);
      for (j = 0; j < sub.length; j++) {
        // Removing the overlay icon leaves a normal-looking card; the card link still works.
        sub[j].parentNode && sub[j].parentNode.removeChild(sub[j]);
      }
      neutralise(list[i], urlOf(who));
    }

    // 2. Cards Ella re-rendered without the marker but whose product we already know.
    list = document.querySelectorAll('.product-item:not([data-pf-cushion])');
    for (i = 0; i < list.length; i++) {
      who = identify(list[i]);
      if (!knows(who.id, who.handle)) continue;
      list[i].setAttribute('data-pf-cushion', '1');
      sub = list[i].querySelectorAll(TRIGGERS);
      for (j = 0; j < sub.length; j++) sub[j].parentNode && sub[j].parentNode.removeChild(sub[j]);
      neutralise(list[i], urlOf(who));
    }

    // 3. Popups whose body arrived over AJAX (quick view, quick add, bulk, cart editor).
    list = document.querySelectorAll(POPUPS);
    for (i = 0; i < list.length; i++) {
      who = identify(list[i]);
      if (!knows(who.id, who.handle)) continue;
      neutralise(list[i], urlOf(who));
      // The cart editor keeps its Add to cart in a sibling block, outside the fragment itself.
      var sib = list[i].nextElementSibling;
      while (sib) {
        if (sib.matches && sib.matches('.product-edit-action')) neutralise(sib, urlOf(who));
        sib = sib.nextElementSibling;
      }
    }
  }

  /* ------------------------------------------------------------- interception */

  // Capture phase, so we run before Ella's own delegated handlers.
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest(TRIGGERS + ',' + BUY) : null;
    if (!t) return;
    // Our own replacement link inherits the button's classes; let it behave like the link it is.
    // A control we parked as inert still goes through the block below, so a scripted click on it
    // cannot reach the cart either.
    if (t.getAttribute('data-pf-guard') === 'link') return;
    var who = identify(t);
    if (!knows(who.id, who.handle) && !isCushionNode(t)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var card = t.closest('[data-pf-cushion]') || t.closest('.product-item') || t.closest(POPUPS);
    // No product page to offer means the click simply does nothing. Better a dead control than one
    // that takes the shopper somewhere they did not ask for.
    var url = urlOf(who) || (card ? urlOf(identify(card)) : null);
    if (url) location.href = url;
  }, true);

  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.getAttribute) return;
    var action = f.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    // The cushion's own product page posts the very same variant ids, with the properties that
    // make them fulfillable - only block forms that live in a card, popup or drawer.
    if (!f.closest || !f.closest(CTX)) return;
    var who = identify(f);
    if (!knows(who.id, who.handle) && !isCushionNode(f)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var url = urlOf(who) || (f.closest('[data-pf-cushion]') ? urlOf(identify(f.closest('[data-pf-cushion]'))) : null);
    if (url) location.href = url;
  }, true);

  /* ------------------------------------------------------------------- wiring */

  var pending = null;
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; sweep(); }, 60);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }

  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
